import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Add, Edit, Delete, Stop } from '@mui/icons-material';
import { useDataContext } from '../contexts/DataContext';
import { formatDateForDateInput, formatDateInputToISO } from '../utils/dateUtils';

const TreatmentsList = ({ patientId, onUpdate }) => {
  const { treatments: allTreatments, addTreatment, updateTreatment, deleteTreatment } = useDataContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    start_date: '',
    end_date: '',
    instructions: '',
    notes: ''
  });

  // Filtrer les traitements pour ce patient
  const treatments = allTreatments.filter(t => t.patientId === patientId);

  const handleOpenDialog = (treatment = null) => {
    if (treatment) {
      setEditingTreatment(treatment);
      setFormData({
        medication_name: treatment.medication_name || '',
        dosage: treatment.dosage || '',
        frequency: treatment.frequency || '',
        start_date: formatDateForDateInput(treatment.start_date),
        end_date: formatDateForDateInput(treatment.end_date),
        instructions: treatment.instructions || '',
        notes: treatment.notes || ''
      });
    } else {
      setEditingTreatment(null);
      setFormData({
        medication_name: '',
        dosage: '',
        frequency: '',
        start_date: formatDateForDateInput(new Date().toISOString()),
        end_date: '',
        instructions: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTreatment(null);
    setFormData({
      medication_name: '',
      dosage: '',
      frequency: '',
      start_date: '',
      end_date: '',
      instructions: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      const treatmentData = {
        patientId: patientId,
        medication_name: formData.medication_name || undefined,
        dosage: formData.dosage || undefined,
        frequency: formData.frequency || undefined,
        start_date: formatDateInputToISO(formData.start_date),
        end_date: formData.end_date ? formatDateInputToISO(formData.end_date) : undefined,
        instructions: formData.instructions || undefined,
        notes: formData.notes || undefined
      };

      if (editingTreatment) {
        await updateTreatment(editingTreatment.id, treatmentData);
      } else {
        await addTreatment(treatmentData);
      }

      handleCloseDialog();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Afficher les erreurs de validation si elles existent
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map(err => err.msg).join('\n');
        alert(`Erreurs de validation:\n${errorMessages}`);
      } else {
        alert('Erreur lors de la sauvegarde. Veuillez vérifier vos données.');
      }
    }
  };

  const handleStopTreatment = async (treatmentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir arrêter ce traitement ?')) {
      try {
        // Mettre à jour le traitement pour le marquer comme arrêté
        const treatment = treatments.find(t => t.id === treatmentId);
        if (treatment) {
          await updateTreatment(treatmentId, { ...treatment, end_date: new Date().toISOString() });
        }
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Erreur lors de l\'arrêt du traitement:', error);
      }
    }
  };

  const handleDelete = async (treatmentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce traitement ?')) {
      try {
        await deleteTreatment(treatmentId);
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isActive = (treatment) => {
    return !treatment.end_date || new Date(treatment.end_date) > new Date();
  };

  const activeTreatments = treatments.filter(isActive);
  const inactiveTreatments = treatments.filter(t => !isActive(t));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Traitements</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouveau Traitement
        </Button>
      </Box>

      {/* Traitements actifs */}
      {activeTreatments.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom color="primary">
            Traitements Actifs
          </Typography>
          {activeTreatments
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((treatment) => (
            <Card key={treatment.id} sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {treatment.medication_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {treatment.dosage} - {treatment.frequency}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Depuis le {formatDate(treatment.start_date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip label="Actif" color="primary" sx={{ mb: 1 }} />
                    <Box>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleOpenDialog(treatment)}
                      >
                        Modifier
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<Stop />}
                        onClick={() => handleStopTreatment(treatment.id)}
                      >
                        Arrêter
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDelete(treatment.id)}
                      >
                        Supprimer
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {treatment.instructions && (
                  <Typography variant="body2" paragraph>
                    <strong>Instructions:</strong> {treatment.instructions}
                  </Typography>
                )}

                {treatment.notes && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Notes:</strong> {treatment.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Traitements inactifs */}
      {inactiveTreatments.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Traitements Terminés
          </Typography>
          {inactiveTreatments
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((treatment) => (
            <Card key={treatment.id} sx={{ mb: 2, opacity: 0.7 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {treatment.medication_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {treatment.dosage} - {treatment.frequency}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Du {formatDate(treatment.start_date)} au {formatDate(treatment.end_date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip label="Terminé" color="default" sx={{ mb: 1 }} />
                    <Box>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleOpenDialog(treatment)}
                      >
                        Modifier
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDelete(treatment.id)}
                      >
                        Supprimer
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {treatment.instructions && (
                  <Typography variant="body2" paragraph>
                    <strong>Instructions:</strong> {treatment.instructions}
                  </Typography>
                )}

                {treatment.notes && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Notes:</strong> {treatment.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {treatments.length === 0 && (
        <Typography variant="body1" color="textSecondary">
          Aucun traitement enregistré
        </Typography>
      )}

      {/* Dialog pour ajouter/modifier un traitement */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTreatment ? 'Modifier le traitement' : 'Nouveau traitement'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du médicament"
                value={formData.medication_name}
                onChange={(e) => setFormData({ ...formData, medication_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dosage"
                value={formData.dosage}
                onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fréquence"
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                placeholder="ex: 2 fois par jour, matin et soir"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de début"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de fin (optionnel)"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instructions"
                multiline
                rows={3}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="Instructions de prise, précautions, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTreatment ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TreatmentsList;
